import { useMemo, useState } from 'react';
import { useAdminI18n } from '@/lib/i18n/use-admin-i18n';
import type { PaginationState, SortingState } from '@tanstack/react-table';
import type { ProjectConfig } from '@cms/models';
import { DynamicTable } from '@/components/custom/table/DynamicTable';
import type {
  DynamicTableColumnConfig,
  DynamicTableControllers,
} from '@/components/custom/table/dynamic-table-types';
import { SearchFilter, StatusFilter } from '@/components/custom/filters';
import { STATUS_OPTIONS } from '@cms/utils';
import { extractApiErrorMessage } from '@/lib/api/extract-api-error-message';
import { useDeleteProjectConfig, useProjectConfigs } from '@/lib/api/project-configs';
import {
  ProjectConfigFormModal,
  type ProjectConfigFormModalMode,
} from './ProjectConfigFormModal';
import {
  AdminTableRowActions,
  PagePrimaryActionButton,
} from '@/components/common/AdminTableRowActions';

type ProjectConfigStatusFilter = (typeof STATUS_OPTIONS)[number]['value'] | '';
const PROJECT_CONFIG_TYPE_OPTIONS = [
  { value: 'css_selector', label: 'CSS Selector' },
  { value: 'xpath', label: 'XPath' },
  { value: 'regex', label: 'Regex' },
] as const;
type ProjectConfigTypeFilter = (typeof PROJECT_CONFIG_TYPE_OPTIONS)[number]['value'] | '';

interface ProjectConfigsTableProps {
  rows: ProjectConfig[];
  controllers?: DynamicTableControllers;
  rowOffset: number;
  onView: (item: ProjectConfig) => void;
  onEdit: (item: ProjectConfig) => void;
  onDelete: (item: ProjectConfig) => void;
  isDeleting: boolean;
  isLoading: boolean;
}

function getOrderNum(item: ProjectConfig): number {
  const maybeCamel = (item as ProjectConfig & { orderNum?: unknown }).orderNum;
  if (typeof item.order_num === 'number') return item.order_num;
  if (typeof maybeCamel === 'number') return maybeCamel;
  return 0;
}

function ProjectConfigsTable(props: Readonly<ProjectConfigsTableProps>) {
  const { rows, controllers, rowOffset, onView, onEdit, onDelete, isDeleting, isLoading } = props;
  const { col, act, page, statusOptions, tableLoading } = useAdminI18n();

  const columns: DynamicTableColumnConfig<ProjectConfig>[] = useMemo(
    () => [
    {
      id: 'no',
      header: col('no'),
      columnType: 'number',
      cell: ({ index }) => rowOffset + index + 1,
      widthMode: 'fit',
      size: 64,
      meta: { className: 'w-16 text-muted-foreground' },
    },
    {
      id: 'key',
      accessorKey: 'key',
      header: col('key'),
      sortable: true,
      columnType: 'text',
      widthMode: 'fit',
      size: 140,
      minSize: 120,
      meta: { className: 'min-w-[120px]' },
    },
    {
      id: 'value',
      accessorKey: 'value',
      header: col('value'),
      columnType: 'text',
      cell: ({ row }) => {
        const value = row.value ?? '';
        return (
          <div className="max-w-[14rem] truncate" title={value}>
            {value}
          </div>
        );
      },
      widthMode: 'fit',
      size: 200,
      minSize: 150,
      meta: { className: 'min-w-[150px]' },
    },
    {
      id: 'type',
      accessorKey: 'type',
      header: col('type'),
      sortable: true,
      columnType: 'badge',
      displayLabelOptions: PROJECT_CONFIG_TYPE_OPTIONS,
      widthMode: 'fit',
      size: 110,
      minSize: 100,
      meta: { className: 'min-w-[100px]' },
    },
    {
      id: 'status',
      accessorKey: 'status',
      header: col('status'),
      sortable: true,
      columnType: 'badge',
      displayLabelOptions: statusOptions,
      widthMode: 'fit',
      size: 100,
      minSize: 90,
      meta: { className: 'min-w-[90px]' },
    },
    {
      id: 'notes',
      accessorKey: 'notes',
      header: col('notes'),
      columnType: 'text',
      cell: ({ row }) => {
        const notes = row.notes ?? '';
        return (
          <div className="max-w-[10rem] truncate" title={notes}>
            {notes || '—'}
          </div>
        );
      },
      widthMode: 'fit',
      size: 150,
      minSize: 120,
      meta: { className: 'min-w-[120px]' },
    },
    {
      id: 'actions',
      header: col('actions'),
      columnType: 'actions',
      cell: ({ row }) => (
        <AdminTableRowActions
          act={act}
          compact
          onView={() => onView(row)}
          onEdit={() => onEdit(row)}
          onDelete={() => onDelete(row)}
          isDeleting={isDeleting}
        />
      ),
      widthMode: 'fit',
      size: 180,
      minSize: 150,
      meta: { className: 'min-w-[150px] text-right' },
    },
    ],
    [act, col, isDeleting, onDelete, onEdit, onView, rowOffset, statusOptions],
  );

  return (
    <DynamicTable<ProjectConfig>
      features={{
        columns: true,
        rows: true,
        sorting: true,
        pagination: true,
        globalFilter: false,
        stripedRows: true,
        fontSize: 'xs',
        compactPadding: true,
      }}
      columns={columns}
      rows={{
        data: rows,
        getRowId: row => row.id,
      }}
      controllers={controllers}
      isLoading={isLoading}
      loadingMessage={tableLoading}
      emptyMessage={page('project_config', 'empty')}
    />
  );
}

export function ProjectConfigPage() {
  const { page, statusOptions, allStatusesOption } = useAdminI18n();
  const query = useProjectConfigs();
  const deleteMutation = useDeleteProjectConfig();
  const [sorting, setSorting] = useState<SortingState>([{ id: 'order_num', desc: false }]);
  const [pagination, setPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: 25 });
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<ProjectConfigStatusFilter>('');
  const [typeFilter, setTypeFilter] = useState<ProjectConfigTypeFilter>('');
  const [formModal, setFormModal] = useState<{
    open: boolean;
    mode: ProjectConfigFormModalMode;
    projectConfig: ProjectConfig | null;
  }>({ open: false, mode: 'create', projectConfig: null });
  const rows = query.data ?? [];

  const onDelete = async (id: string) => {
    try {
      await deleteMutation.mutateAsync(id);
    } catch (error: unknown) {
      alert(extractApiErrorMessage(error));
    }
  };

  const filteredRows = useMemo(() => {
    let list = rows;
    const term = search.trim().toLowerCase();
    if (term) {
      list = list.filter(item => {
        const key = item.key?.toLowerCase() ?? '';
        const value = item.value?.toLowerCase() ?? '';
        const type = item.type?.toLowerCase() ?? '';
        const notes = item.notes?.toLowerCase() ?? '';
        return key.includes(term) || value.includes(term) || type.includes(term) || notes.includes(term);
      });
    }
    if (statusFilter) {
      list = list.filter(item => item.status === statusFilter);
    }
    if (typeFilter) {
      list = list.filter(item => item.type === typeFilter);
    }
    return list;
  }, [rows, search, statusFilter, typeFilter]);

  const sortedRows = useMemo(() => {
    if (!sorting.length) return filteredRows;
    const { id, desc } = sorting[0]!;
    return [...filteredRows].sort((a, b) => {
      if (id === 'order_num') {
        const cmp = getOrderNum(a) - getOrderNum(b);
        return desc ? -cmp : cmp;
      }
      if (id === 'status') {
        const cmp = (a.status ?? '').localeCompare(b.status ?? '');
        return desc ? -cmp : cmp;
      }
      if (id === 'type') {
        const cmp = (a.type ?? '').localeCompare(b.type ?? '');
        return desc ? -cmp : cmp;
      }
      const av =
        id === 'value' ? a.value ?? '' : id === 'notes' ? a.notes ?? '' : a.key ?? '';
      const bv =
        id === 'value' ? b.value ?? '' : id === 'notes' ? b.notes ?? '' : b.key ?? '';
      const cmp = String(av).localeCompare(String(bv));
      return desc ? -cmp : cmp;
    });
  }, [filteredRows, sorting]);

  const totalRecords = sortedRows.length;
  const pageSize = pagination.pageSize;
  const pageCount = Math.max(1, Math.ceil(Math.max(1, totalRecords) / pageSize));
  const safePageIndex = Math.min(pagination.pageIndex, pageCount - 1);

  const pageState: PaginationState = {
    pageIndex: safePageIndex,
    pageSize,
  };

  const paginatedRows = useMemo(() => {
    const start = safePageIndex * pageSize;
    return sortedRows.slice(start, start + pageSize);
  }, [sortedRows, safePageIndex, pageSize]);

  const controllers: DynamicTableControllers = {
    sort: {
      state: sorting,
      onSortingChange: updater => {
        setSorting(prev => (typeof updater === 'function' ? updater(prev) : updater));
        setPagination(prev => ({ ...prev, pageIndex: 0 }));
      },
    },
    pagination: {
      state: pageState,
      pageCount,
      rowCount: totalRecords,
      onPaginationChange: updater => {
        setPagination(prev => (typeof updater === 'function' ? updater(prev) : updater));
      },
    },
  };

  return (
    <div className="space-y-2 p-2">
      <div>
        <h1 className="text-3xl font-bold">{page('project_config', 'title')}</h1>
        <p className="mt-1 text-sm text-muted-foreground">{page('project_config', 'subtitle')}</p>
      </div>

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div />
        <PagePrimaryActionButton
          onClick={() => setFormModal({ open: true, mode: 'create', projectConfig: null })}
        >
          {page('project_config', 'new')}
        </PagePrimaryActionButton>
      </div>

      <div className="glass-card overflow-hidden">
        <div className="flex flex-wrap items-end gap-2 border-b border-border p-3">
          <SearchFilter
            id="project-config-search"
            label="Search"
            value={search}
            onChange={v => {
              setSearch(v);
              setPagination(prev => ({ ...prev, pageIndex: 0 }));
            }}
            placeholder="Search by key, value, type, notes..."
            className="[&_input]:h-9 [&_input]:py-1.5 [&_input]:text-sm"
          />
          <StatusFilter
            id="project-config-type-filter"
            label="Type"
            value={typeFilter}
            onChange={v => {
              setTypeFilter(v as ProjectConfigTypeFilter);
              setPagination(prev => ({ ...prev, pageIndex: 0 }));
            }}
            options={PROJECT_CONFIG_TYPE_OPTIONS}
            emptyOption={{ value: '', label: 'All types' }}
            className="[&_button]:h-9 [&_input]:h-9 [&_input]:py-1.5 [&_input]:text-sm"
          />
          <StatusFilter
            id="project-config-status-filter"
            label="Status"
            value={statusFilter}
            onChange={v => {
              setStatusFilter(v as ProjectConfigStatusFilter);
              setPagination(prev => ({ ...prev, pageIndex: 0 }));
            }}
            options={statusOptions}
            emptyOption={allStatusesOption}
            className="[&_button]:h-9 [&_input]:h-9 [&_input]:py-1.5 [&_input]:text-sm"
          />
        </div>
        <ProjectConfigsTable
          rows={paginatedRows}
          controllers={controllers}
          rowOffset={pageState.pageIndex * pageState.pageSize}
          onView={item => setFormModal({ open: true, mode: 'view', projectConfig: item })}
          onEdit={item => setFormModal({ open: true, mode: 'edit', projectConfig: item })}
          onDelete={item => void onDelete(item.id)}
          isDeleting={deleteMutation.isPending}
          isLoading={query.isLoading}
        />
      </div>

      <ProjectConfigFormModal
        open={formModal.open}
        onClose={() => setFormModal(prev => ({ ...prev, open: false, projectConfig: null }))}
        mode={formModal.mode}
        projectConfig={formModal.projectConfig}
        onSuccess={() => setFormModal(prev => ({ ...prev, open: false, projectConfig: null }))}
      />
    </div>
  );
}
